/**
 * ReplyReadyView — "it is done in Epic; the reply is drafted, not sent."
 *
 * Story 26739. Visual contract: artboard (5) in
 * `tasks/design/step-views.html`.
 *
 * Proof first, then what changed, then the hop to the draft. Proof leads
 * because the Servicer is about to put their name on an email saying the work
 * happened — they should see the endorsement number before they see the
 * button, not after.
 *
 * THIS PANE CANNOT SEND MAIL, AND THAT IS THE POINT.
 *
 * There is no Send control here and there must never be one. `onOpenDraft`
 * opens the draft in Outlook; the Servicer sends it, from Outlook, as
 * themselves. Two reasons, and both survive any future convenience argument:
 *
 *   1. Sending is the moment the firm speaks to a client. It belongs to a
 *      person, in the client of record, with their signature and their sent
 *      items — not to a pane that could fire on a mis-click.
 *   2. ora has no Mail.Send identity to do it with, and will not get one.
 *      Notifications reach a Servicer by being WRITTEN into their mailbox
 *      under Mail.ReadWrite (ADO 28182); nothing in the platform sends. That
 *      mechanism cannot address a mailbox outside the tenant at all, so it is
 *      not a route to a client or a carrier even in principle.
 *
 * `replyReadyHasNoSend.test.tsx` asserts nothing in the rendered output is a
 * send affordance. It is a guard against a well-meaning "one less click"
 * change, which is exactly how this control would come back.
 *
 * No Office.js, no Remix server import (design Constraint 20) — `onOpenDraft`
 * is a callback the add-in wires to `openMessageInOutlook`, and ora wires to a
 * disabled hint because a browser tab cannot open an Outlook draft.
 */
import type { ReactNode } from "react";
import { StepViewFrame, identityLine } from "./StepViewFrame";
import { STATUS_LABELS } from "../statusLabels";

export const OPEN_DRAFT_LABEL = "Open the draft in Outlook ↗";
export const SEE_THE_PROOF_LABEL = "See the proof ›";

export interface ReplyReadyViewProps {
  clientName?: string | null;
  policyNumber?: string | null;
  /** "Done in Epic. Endorsement #EN-… recorded 11:42." */
  proofSentence?: ReactNode;
  /** Shown INSTEAD of proof/bullets when the parked run is not clean (warnings). Warning tone. */
  warningSentence?: ReactNode;
  /** "See the proof ›" — omit for no hop. */
  onOpenProof?: () => void;
  /** What changed, in the Servicer's words. Three lines on the artboard. */
  bullets?: readonly ReactNode[];
  /** Opens the draft. NOT a send. */
  onOpenDraft?: () => void;
  /** Override the button label. */
  openDraftLabel?: string;
  /**
   * Disables the hop and explains why — ora's web host passes this, because a
   * browser tab cannot open an Outlook draft.
   */
  openDraftHint?: ReactNode | null;
  children?: ReactNode;
}

export function ReplyReadyView({
  clientName,
  policyNumber,
  proofSentence,
  warningSentence,
  onOpenProof,
  bullets,
  onOpenDraft,
  openDraftLabel = OPEN_DRAFT_LABEL,
  openDraftHint,
  children,
}: ReplyReadyViewProps) {
  const draftDisabled = openDraftHint != null || !onOpenDraft;

  return (
    <StepViewFrame
      view="reply_ready"
      // The Monitor grid's words and white pill for this state (2026-10-05).
      eyebrow={STATUS_LABELS.pending_email_send}
      tone="quiet"
      headline={identityLine(clientName, policyNumber)}
    >
      {warningSentence ? (
        <p
          className="rounded-md border border-primary-warning-200 bg-primary-warning-50 px-2.5 py-2 text-[12.5px] font-medium leading-relaxed text-primary-warning-700"
          data-testid="reply-ready-warning"
        >
          {warningSentence}
        </p>
      ) : null}

      {warningSentence && onOpenProof ? (
        <button
          type="button"
          onClick={onOpenProof}
          className="w-fit border-b-2 border-sunrise text-[11.5px] font-semibold text-primary-gray-light-900"
          data-testid="reply-ready-proof-link"
        >
          {SEE_THE_PROOF_LABEL}
        </button>
      ) : null}

      {proofSentence ? (
        <div
          className="flex flex-col gap-1.5 rounded-lg border border-sunrise border-t-[3px] bg-[#FFFDF2] px-3 py-2.5"
          data-testid="reply-ready-proof"
        >
          <p className="text-xs leading-relaxed text-primary-gray-light-900">
            {proofSentence}
          </p>
          {onOpenProof ? (
            <button
              type="button"
              onClick={onOpenProof}
              className="w-fit border-b-2 border-sunrise text-[11.5px] font-semibold text-primary-gray-light-900"
              data-testid="reply-ready-proof-link"
            >
              {SEE_THE_PROOF_LABEL}
            </button>
          ) : null}
        </div>
      ) : null}

      {bullets && bullets.length > 0 ? (
        <ul
          className="list-disc pl-4 text-[11.5px] leading-relaxed text-primary-gray-light-700"
          data-testid="reply-ready-bullets"
        >
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      ) : null}

      {children}

      <button
        type="button"
        onClick={draftDisabled ? undefined : onOpenDraft}
        disabled={draftDisabled}
        className="self-stretch rounded-full border border-base-black bg-sunrise px-2.5 py-2 text-center text-xs font-semibold text-primary-gray-light-900 disabled:cursor-not-allowed disabled:opacity-60"
        data-testid="reply-ready-open-draft"
      >
        {openDraftLabel}
      </button>

      {openDraftHint ? (
        <p
          className="text-2xs text-primary-gray-light-600"
          data-testid="reply-ready-open-draft-hint"
        >
          {openDraftHint}
        </p>
      ) : null}
    </StepViewFrame>
  );
}

export default ReplyReadyView;
